// web/src/routes/dashboard/ServiceProviderDashboard.jsx
// Today, for a farrier or a vet. Answers: "Who am I seeing today?"
//
// The planner idiom, and the shortest of the five: a visiting provider wants
// the list of horses and the ones somebody has flagged, and nothing else.
//
// The flagged concerns lead. A pinned note is somebody at the barn saying
// "look at this one" — putting it under a roster of forty horses means it gets
// read after the visit rather than before it.
//
// The page flips, and "Due this day" is what flipping changes. Everything else
// here is a standing list — the horses at the barn, the notes somebody pinned —
// so without it the little month would be a control that did nothing, which is
// worse than not offering one.

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import MiniMonth from '../../components/dashboard/MiniMonth';
import { DayHeader, Panel, Empty, Row, Rows, PlannerGrid, PlannerSkeleton } from '../../components/dashboard/Planner';

export default function ServiceProviderDashboard() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'provider', date ?? 'today'],
    queryFn: () => api.get(date ? `/dashboard/provider?date=${date}` : '/dashboard/provider'),
    placeholderData: (prev) => prev,
  });

  const today = data?.today ?? null;
  const shown = data?.date ?? date ?? today;

  const goTo = (d) => {
    const next = new URLSearchParams(params);
    if (!d || d === today) next.delete('date');
    else next.set('date', d);
    setParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <DayHeader today={today} date={shown} onToday={() => goTo(null)} />

      {isLoading ? (
        <PlannerSkeleton />
      ) : (
        <PlannerGrid aside={<MiniMonth today={today} selected={shown} onPick={goTo} />}>
          <DueThisDay items={data?.dueThisDay} />
          <Concerns items={data?.flaggedConcerns} />
          <Horses items={data?.horses} />
        </PlannerGrid>
      )}
    </div>
  );
}

/**
 * What the barn has recorded as due on the day you are looking at.
 *
 * This is the only thing on the page that varies by day, and it is here because
 * of that: the little month was a control that changed nothing. It is also the
 * closest thing the barn holds to a farrier's round — the horses whose feet are
 * booked for the day they are standing in the aisle.
 */
function DueThisDay({ items = [] }) {
  return (
    <Panel title="Due this day" count={items.length}>
      {items.length === 0 ? (
        <Empty>
          Nothing recorded as due.{' '}
          <Link to="/horses" className="text-hunter hover:underline">All horses →</Link>
        </Empty>
      ) : (
        <Rows>
          {items.map((d) => (
            <Row key={d.id} to={`/horses/${d.horseId}`} className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{d.horseName}</span>
                <span className="text-ink-500"> · {d.recordType}</span>
              </span>
              {d.stall && <span className="shrink-0 text-xs text-ink-400">stall {d.stall}</span>}
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function Concerns({ items = [] }) {
  return (
    <Panel title="Flagged for you" count={items.length}>
      {items.length === 0 ? (
        <Empty>Nothing flagged.</Empty>
      ) : (
        <Rows>
          {items.map((c) => (
            <Row key={c.id} to={`/horses/${c.horseId}`}>
              <span className="block font-medium">{c.horseName}</span>
              <span className="block text-ink-600 line-clamp-2">{c.body}</span>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function Horses({ items = [] }) {
  return (
    <Panel title="Horses at this barn" count={items.length}>
      {items.length === 0 ? (
        <Empty>No horses on the books.</Empty>
      ) : (
        <Rows>
          {items.map((h) => (
            <Row key={h.id} to={`/horses/${h.id}`} className="flex items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{h.name}</span>
                {h.stall && <span className="text-ink-400"> · stall {h.stall}</span>}
              </span>
              <span className="shrink-0 text-xs text-ink-400">
                {/* "never" is the useful answer here, not a blank: a horse this
                    provider has not seen is the one to ask about. */}
                last seen {h.lastVisit ?? 'never'}
              </span>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}
